import { Check, ChevronsUpDown, LayoutGrid, Server } from 'lucide-react';
import React from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { buildForgeAppLinks, buildForgeEnvironmentLinks, LocationLike } from './forgeContextLinks';

/**
 * Two chips in the sidebar footer, one per axis: which tool you are in, and which environment it
 * is served from. Each opens its own menu. They replace the two always-visible segmented rows
 * (seven targets) this file's predecessor rendered.
 *
 * The axes are independent — you change tool for one reason and environment for another — so each
 * gets its own control and its own short menu, and the chip you press is the chip that answers.
 *
 * The two sit on their own rows, styled as sidebar footer rows rather than as foreign controls:
 * the same padding, height, type and hover as `.sidebar-footer-link` beside them, a leading icon
 * naming the axis, and a trailing chevron so they still read as openers rather than destinations.
 *
 * Neither row is coloured by environment: both take the ordinary neutral sidebar styling, and the
 * environment's name — `production` / `staging` / `local`, or `prd` / `stg` / `loc` on the
 * collapsed rail — is what says where you are. Every name is lowercase in the data, not via
 * `text-transform`, so the visible text and the accessible name are the same string. The menus
 * mark the current option with a tick, as any menu does.
 *
 * Both menus navigate with real anchors, exactly as the rows did: an environment switch is
 * cross-origin, and a tool switch loads that tool's shell. The document is replaced either way,
 * so no menu close can strand focus; Escape and outside-click stay on Radix's own restore, which
 * returns focus to the chip that opened the menu.
 */

interface ForgeContextOptionProps {
  href: string;
  title: string;
  label: string;
  description: string;
  isActive: boolean;
}

const ForgeContextOption: React.FC<ForgeContextOptionProps> = ({
  href,
  title,
  label,
  description,
  isActive,
}) => {
  // The description is a description, not part of the name: a menu item called "Forge" reads as
  // "Forge, Labeling tools, task lists and dashboards", not as one run-on sentence.
  const descriptionId = React.useId();

  return (
    <DropdownMenuItem asChild>
      <a
        className={`forge-context-option ${isActive ? 'is-current' : ''}`}
        href={href}
        title={title}
        aria-current={isActive ? 'page' : undefined}
        aria-describedby={descriptionId}
        aria-label={label}
      >
        <span className="forge-context-option-mark" aria-hidden="true">
          {isActive ? <Check /> : null}
        </span>
        <span className="forge-context-option-text">
          <span className="forge-context-option-name">{label}</span>
          <span className="forge-context-option-description" id={descriptionId}>
            {description}
          </span>
        </span>
      </a>
    </DropdownMenuItem>
  );
};

interface ForgeSwitcherChipProps {
  /** Leading icon, matching the icon treatment of every other row in the footer. */
  Icon: React.ComponentType<{ className?: string }>;
  /** Full name, shown in the expanded sidebar and the mobile drawer. */
  label: string;
  /** Abbreviation for the 26px collapsed rail. */
  shortLabel: string;
  /** Hover text, which the collapsed rail relies on to expand the abbreviation. */
  title: string;
  ariaLabel: string;
  menuLabel: string;
  menuNote?: string;
  options: ForgeContextOptionProps[];
}

const ForgeSwitcherChip: React.FC<ForgeSwitcherChipProps> = ({
  Icon,
  label,
  shortLabel,
  title,
  ariaLabel,
  menuLabel,
  menuNote,
  options,
}) => {
  // `role="menu"` exposes only menuitem/group/separator children, so a bare <p> inside it is
  // sighted-only. Naming it from the content is what puts the sentence in the accessibility tree.
  const noteId = React.useId();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="forge-context-chip" title={title} aria-label={ariaLabel}>
          <Icon className="forge-context-chip-icon" aria-hidden="true" />
          <span className="forge-context-chip-label">
            <span className="forge-context-label-full">{label}</span>
            <span className="forge-context-label-short">{shortLabel}</span>
          </span>
          <ChevronsUpDown className="forge-context-chip-caret" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="forge-context-menu"
        side="top"
        align="start"
        aria-describedby={menuNote ? noteId : undefined}
      >
        <DropdownMenuLabel className="forge-context-menu-label">{menuLabel}</DropdownMenuLabel>
        {options.map((option) => (
          <ForgeContextOption key={option.href} {...option} />
        ))}
        {menuNote ? (
          <p className="forge-context-menu-note" id={noteId}>
            {menuNote}
          </p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ForgeContextSwitcher: React.FC<{ location?: LocationLike }> = ({
  location = window.location,
}) => {
  const { hasInternalAccess } = useAuthContext();

  if (!hasInternalAccess) {
    return null;
  }

  const environments = buildForgeEnvironmentLinks(location);
  const apps = buildForgeAppLinks(location, { includeArchon: true });
  const currentEnvironment = environments.find((environment) => environment.isActive);
  // `control` is the catch-all for any path outside /forge, /eval and /archon, so exactly one tool
  // is always active; the first entry is a sane read if that predicate ever loosens.
  const currentApp = apps.find((app) => app.isActive) ?? apps[0];

  // An origin that is neither production, staging, nor a dev port (a preview deploy, say) has no
  // environment to name, and saying so beats highlighting nothing.
  const environmentLabel = currentEnvironment?.label ?? 'unknown';
  const environmentShortLabel = currentEnvironment?.shortLabel ?? '?';

  return (
    // One row each, stacked, so neither axis is squeezed into half a rail and the pair reads as
    // two more rows of the footer list rather than one two-part widget.
    <div className="forge-context-switchers">
      <ForgeSwitcherChip
        Icon={Server}
        label={environmentLabel}
        shortLabel={environmentShortLabel}
        title={`environment: ${environmentLabel}`}
        ariaLabel={`Change environment. Current: ${environmentLabel}.`}
        menuLabel="environment"
        menuNote="Switching environment keeps this page, filters and all."
        options={environments}
      />
      <ForgeSwitcherChip
        Icon={LayoutGrid}
        label={currentApp.label}
        shortLabel={currentApp.shortLabel}
        title={`tool: ${currentApp.label}`}
        ariaLabel={`Change tool. Current: ${currentApp.label}.`}
        menuLabel="tool"
        options={apps}
      />
    </div>
  );
};
